import { TFunction } from "i18next";
import { z } from "zod";
import { nullableNumber } from "../../../utils";

export const getUpdateCaptureInfoFormSchema = (t: TFunction) => {
  const numberField = nullableNumber(t("generic.errors.invalid-number"));
  return z.object({
    capture: z.object({
      name: z
        .string()
        .max(255)
        .min(1, { message: t("captures.capture.errors.name-required") }),

      entityName: z
        .string()
        .min(1, { message: t("captures.capture.errors.entity-name-required") }),

      entityAcronym: z
        .string()
        .min(1, { message: t("captures.capture.errors.entity-acronym-required") }),

      managementUnitName: z
        .string()
        .nullable()
        .optional(),
      
      aquaEpsDesignation: z.string().max(255).nullable().optional(),
      entityDesignation: z.string().max(255).nullable().optional(),
      licensingDesignation: z.string().max(255).nullable().optional(),
      licensingDesignationSystem: z
        .enum(["SNIRH", "TUA"], {
          errorMap: () => ({
            message: t("captures.capture.general.identification.errors.licensing-system-invalid"),
          }),
        })
        .nullable()
        .optional(),
      otherDesignations: z
        .array(
          z.object({
            label: z
              .string()
              .min(1, { message: t("captures.capture.general.identification.errors.other-designation-label-required") })
              .max(100),
            value: z
              .string()
              .min(1, { message: t("captures.capture.general.identification.errors.other-designation-value-required") })
              .max(255),
          })
        )
        .max(20, { message: t("captures.capture.general.identification.errors.other-designations-max") })
        .optional()
        .default([]),
    }),

    capturePurposes:z.object({
      supplyTypeId: z.number().positive().nullable().optional(),
      purposeId: z.number().positive().nullable().optional(),
      usageId: z.number().positive().nullable().optional(),
    }),

    captureLocation: z.object({
      mapReference: z.string().nullable().optional(),

      coordinates: z.discriminatedUnion("crs", [
        z.object({
          crs: z.literal("ETRS89"),
          xETRS: z    
            .number({
              required_error: t("captures.capture.general.location-details.errors.xETRS-required"),
              invalid_type_error: t("captures.capture.general.location-details.errors.xETRS-invalid"),
            }),

          yETRS: z
            .number({
              required_error: t("captures.capture.general.location-details.errors.yETRS-required"),
              invalid_type_error: t("captures.capture.general.location-details.errors.yETRS-invalid"),
            }),
          zETRS: numberField.optional(),
        }),
        z.object({
          crs: z.literal("WGS84"),
          longitude: z
            .number({
              required_error: t("captures.capture.general.location-details.errors.longitude-required"),
              invalid_type_error: t("captures.capture.general.location-details.errors.longitude-invalid"),
            }),
          latitude: z
            .number({
              required_error: t("captures.capture.general.location-details.errors.latitude-required"),
              invalid_type_error: t("captures.capture.general.location-details.errors.latitude-invalid"),
            }),
          elevation: numberField.optional(),
        }),
      ]),
    }), 

    constructionDetails: z.object({
      subsurfaceCaptureTypeId: z.number().positive().nullable().optional(),
      drillingMethodId: z.number().positive().nullable().optional(),
      boreholeDepth: numberField.optional(),
      casingDepth: numberField.optional(),
      firstDrainDepth: numberField.optional(),
      lastDrainDepth: numberField.optional(),
      boreholeDiameter: numberField.optional(),
      casingDiameter: numberField.optional(),
      casingMaterialId: z.number().positive().nullable().optional(),
      constructionYear: numberField.optional(),
    }),

    hydrogeologicContext: z.object({
      watershedId: z.number().positive().nullable().optional(),
      subWatershedId: z.number().positive().nullable().optional(),
      hydrogeologicUnitId: z.number().positive().nullable().optional(),
      aquiferSystemId: z.number().positive().nullable().optional(),
      aquiferUnitId: z.number().positive().nullable().optional(),
      geologyMassId: z.number().positive().nullable().optional(),
      waterBodyId: z.number().positive().nullable().optional(),
    })
  });
};

export type UpdateCaptureInfoForm = z.infer<ReturnType<typeof getUpdateCaptureInfoFormSchema>>;

export type CaptureInfoApiPayload = Omit<UpdateCaptureInfoForm, "capture"> & {
  capture: Omit<UpdateCaptureInfoForm["capture"], "otherDesignations"> & {
    otherDesignations: Record<string, string>;
  };
};